export function createLocalRemoteReconciliationHelpers({
  normalizeDisplayName,
  getFirstRecordValue,
  getRecordMediaId,
  getRecordStoredFileName,
  getRecordTranscriptFileId,
  getEffectiveTranscriptRecord,
  buildCaseFileItems,
  dedupeCaseFileRecords,
  chooseBetterCaseFileRecord,
  isMediaCaseRecord,
  isTranscriptCaseRecord,
  mapRemoteScanStatus,
  getRecordScanStatus,
  isVirusStatus,
  isSuccessfulStatus
}) {
  function localUploadItemMatchesRemoteRecord(item, record) {
    if (!item || !record) {
      return false;
    }

    const localMediaId = normalizeDisplayName(item?.mediaId);
    const localTranscriptFileId = normalizeDisplayName(item?.transcriptFileId);
    const remoteMediaId = normalizeDisplayName(getRecordMediaId(record));
    const remoteTranscriptFileId = normalizeDisplayName(getRecordTranscriptFileId(record));

    if (localMediaId && remoteMediaId && localMediaId === remoteMediaId) {
      return true;
    }

    if (localTranscriptFileId && remoteTranscriptFileId && localTranscriptFileId === remoteTranscriptFileId) {
      return true;
    }

    return false;
  }

  function localUploadItemMatchesRemoteItem(item, remoteItem) {
    if (!item || !remoteItem) {
      return false;
    }

    const transcriptRecord = getEffectiveTranscriptRecord(remoteItem?.mediaRecord, remoteItem?.transcriptRecord ?? null);
    return localUploadItemMatchesRemoteRecord(item, remoteItem?.mediaRecord)
      || localUploadItemMatchesRemoteRecord(item, transcriptRecord);
  }

  function isLocalUploadItemAlreadyPersisted(item, remoteRecords = []) {
    if (!item) {
      return false;
    }

    if (normalizeDisplayName(item.mediaId) || normalizeDisplayName(item.transcriptFileId)) {
      return true;
    }

    return buildCaseFileItems(Array.isArray(remoteRecords) ? remoteRecords : [])
      .some((remoteItem) => localUploadItemMatchesRemoteItem(item, remoteItem));
  }

  function mergeCaseFileSnapshots(currentRecords, incomingRecords, buildCaseFileKey) {
    const merged = new Map();

    (currentRecords ?? []).forEach((record) => {
      const key = buildCaseFileKey(record);
      if (key) {
        merged.set(key, record);
      }
    });

    (incomingRecords ?? []).forEach((record) => {
      const key = buildCaseFileKey(record);
      if (!key) {
        return;
      }

      const existing = merged.get(key);
      merged.set(key, existing ? chooseBetterCaseFileRecord(existing, { ...existing, ...record }) : record);
    });

    return dedupeCaseFileRecords(Array.from(merged.values()));
  }

  function annotateRemoteCaseFileItems(caseFiles, mediaItems) {
    return buildCaseFileItems(caseFiles).map((item) => {
      const mediaRecord = item?.mediaRecord ?? null;
      const recordMediaId = normalizeDisplayName(mediaRecord?.mediaId);
      const recordStoredName = getRecordStoredFileName(mediaRecord);
      const localUploadItem = (mediaItems ?? []).find((uploadItem) => {
        const localMediaId = normalizeDisplayName(uploadItem?.mediaId);
        const localStoredName = normalizeDisplayName(uploadItem?.mediaStoredFileName);

        return Boolean(mediaRecord) && (
          (recordMediaId && localMediaId === recordMediaId) ||
          (recordStoredName && localStoredName === recordStoredName)
        );
      }) ?? null;

      return {
        ...item,
        localUploadItem
      };
    });
  }

  function findRemoteRecord(records, fileType, prefixedName, mediaId = '') {
    const normalizedPrefixed = normalizeDisplayName(prefixedName);
    const normalizedType = normalizeDisplayName(fileType);
    const normalizedMediaId = normalizeDisplayName(mediaId);

    if (!normalizedMediaId && !normalizedPrefixed) {
      return null;
    }

    return dedupeCaseFileRecords(records ?? []).reduce((bestRecord, record) => {
      const recordType = normalizeDisplayName(getFirstRecordValue(record, ['fileType', 'contentTypeGroup', 'documentType']));
      const storedName = getRecordStoredFileName(record);
      const recordMediaId = normalizeDisplayName(getRecordMediaId(record));
      const typeMatches = normalizedType === 'media'
        ? isMediaCaseRecord(record)
        : normalizedType === 'transcript'
          ? isTranscriptCaseRecord(record) || Boolean(getEffectiveTranscriptRecord(null, record))
          : recordType === normalizedType;

      const matched = typeMatches && (
        (normalizedMediaId && recordMediaId === normalizedMediaId) ||
        (normalizedPrefixed && storedName === normalizedPrefixed)
      );

      return matched ? chooseBetterCaseFileRecord(bestRecord, record) : bestRecord;
    }, null);
  }

  function mergeRemoteStatuses(currentItems, remoteFiles) {
    return (currentItems ?? []).map((item) => {
      const mediaStoredFileName = item.mediaStoredFileName || '';
      const mediaRecord = findRemoteRecord(remoteFiles, 'Media', mediaStoredFileName, item.mediaId);
      const matchedTranscriptRecord = item.transcriptEnabled && item.transcriptFile
        ? findRemoteRecord(remoteFiles, 'Transcript', item.transcriptStoredFileName || '', item.mediaId)
        : null;
      const transcriptRecord = getEffectiveTranscriptRecord(mediaRecord, matchedTranscriptRecord);

      const nextMediaScanStatus = mediaRecord ? mapRemoteScanStatus(getRecordScanStatus(mediaRecord)) : item.scanStatus;
      const nextTranscriptScanStatus = transcriptRecord ? mapRemoteScanStatus(getRecordScanStatus(transcriptRecord)) : item.transcriptScanStatus;
      const nextMediaUploadStatus = mediaRecord ? 'completed' : item.mediaUploadStatus;
      const nextTranscriptUploadStatus = item.transcriptEnabled
        ? (transcriptRecord ? 'completed' : item.transcriptUploadStatus)
        : 'not-applicable';
      const nextMediaId = getRecordMediaId(mediaRecord) || item.mediaId;
      const nextTranscriptFileId = getRecordTranscriptFileId(transcriptRecord) || item.transcriptFileId;
      const hasVirus = isVirusStatus(nextMediaScanStatus) || isVirusStatus(nextTranscriptScanStatus);
      const isMediaComplete = isSuccessfulStatus(nextMediaScanStatus);
      const isTranscriptComplete = !item.transcriptEnabled || isSuccessfulStatus(nextTranscriptScanStatus);
      const isComplete = isMediaComplete && isTranscriptComplete && !hasVirus;

      let nextProgress = item.progress;
      if (hasVirus || isSuccessfulStatus(nextMediaScanStatus)) {
        nextProgress = 100;
      } else if (nextTranscriptUploadStatus === 'completed' || isSuccessfulStatus(nextTranscriptScanStatus)) {
        nextProgress = Math.max(nextProgress, 95);
      } else if (nextMediaUploadStatus === 'completed') {
        nextProgress = Math.max(nextProgress, 80);
      }

      return {
        ...item,
        mediaUploadStatus: nextMediaUploadStatus,
        scanStatus: nextMediaScanStatus,
        transcriptUploadStatus: nextTranscriptUploadStatus,
        transcriptScanStatus: nextTranscriptScanStatus,
        mediaId: nextMediaId,
        transcriptFileId: nextTranscriptFileId,
        progress: nextProgress,
        latestError: hasVirus ? item.latestError : item.latestError,
        isComplete
      };
    });
  }

  return {
    localUploadItemMatchesRemoteRecord,
    localUploadItemMatchesRemoteItem,
    isLocalUploadItemAlreadyPersisted,
    mergeCaseFileSnapshots,
    annotateRemoteCaseFileItems,
    findRemoteRecord,
    mergeRemoteStatuses
  };
}
